// Perfil y preferencias.
//
// Lo que hace distinta a esta pantalla de un formulario de perfil
// cualquiera: **cada campo dice qué cambia**.
//
// "Tolerancia al color: sobrio / normal / arriesgado" no significa nada
// solo. "Sobrio — un solo color fuerte por conjunto" sí, porque el
// usuario puede predecir el efecto antes de guardar y culparme después
// si no pasa.
//
// Y los campos que HOY no hacen nada están agrupados aparte y dicen que
// no hacen nada. Es lo contrario de lo que hace un formulario de perfil
// típico, que sugiere que todo lo que llenás mejora algo.

import { useEffect, useState } from "react";
import * as api from "./api";
import { cargarOpciones, invalidarOpciones } from "./opciones";
import { useAuth } from "./AuthContext";
import Version from "./Version";
import MisListas from "./MisListas";
import { t, tCat, tError, useIdioma, fecha as fechaLocal } from "./i18n";
import { useFocoAtrapado } from "./foco";
import { pais } from "./paises";

/* QUÉ SE LE DICE A ALGUIEN SOBRE EL IDIOMA QUE ELIGE.
 *
 * ⚠️  ESTE TEXTO ES LA MITAD DEL TRABAJO DE i18n, NO UN ADORNO.
 *
 * Los 12 idiomas están COMPLETOS desde el 2026-08-25: catálogo (170
 * entradas), interfaz (435 claves) y los 108 códigos de error. Lo que
 * falta no es traducción sino REVISIÓN: ninguna la leyó un hablante
 * nativo, y el vocabulario de ropa es el más regional que hay.
 *
 * Ese matiz es el que el aviso tiene que transmitir ahora, y por eso
 * cambió: decir "todavía está en español" cuando ya no lo está
 * desalienta de usar algo que funciona.
 *
 * ⚠️  ESTE COMENTARIO SE EQUIVOCÓ DOS VECES, Y LAS DOS POR LO MISMO.
 *     Dijo "4 archivos, ~682 textos" y después "solo el catálogo, ~314
 *     textos siguen en español". Las dos veces era cierto al
 *     escribirse y falso una semana después. Un comentario que describe
 *     el ESTADO de algo envejece hacia la mentira solo; el que describe
 *     una DECISIÓN, no. Cuando se pueda, escribir lo segundo.
 *
 * La lista de abajo tiene que seguir a `CARGADORES` en src/i18n.js. */
const CON_ARCHIVO = ["es","en","pt","fr","ru","zh","ja","ko","hi","bn","ur","ar"];

function avisoDeIdioma(codigo) {
  /* ⚠️  ESTE AVISO DIJO ALGO FALSO DURANTE UN DÍA, Y ES LA CLASE DE
   *     MENTIRA QUE NADIE REPORTA.
   *
   *     Decía "se traducen los nombres de prenda, colores y ocasiones;
   *     el resto de la interfaz todavía está en español". Era cierto
   *     cuando se escribió y dejó de serlo el 2026-08-25, cuando las
   *     435 claves de pantalla se tradujeron a los once idiomas. El
   *     texto siguió ahí, avisando de una limitación que ya no existía
   *     — o sea, desalentando de usar algo que funcionaba.
   *
   *     La lección: un aviso sobre el estado de una función tiene que
   *     cambiar EN EL MISMO COMMIT que la función. Si no, envejece
   *     hacia la mentira sin que nada falle.
   *
   * Lo que SÍ sigue siendo verdad, y por eso el aviso no desaparece:
   * ninguna de las once traducciones la revisó un hablante nativo. */
  if (codigo === "es") {
    return t("perfil.idiomaEsCompleto", "La app está completa en español.");
  }
  if (CON_ARCHIVO.includes(codigo)) {
    return t(
      "perfil.idiomaCompleto",
      "La app está completa en este idioma. La traducción todavía no la revisó una persona que lo hable."
    );
  }
  /* Los doce tienen archivo. Esta rama queda para el idioma número
   * trece, el día que se agregue a IDIOMAS_APP antes que su archivo. */
  return t(
    "perfil.idiomaSinTraducir",
    "Este idioma todavía no está traducido: la app se muestra en inglés, y lo que falte, en español."
  );
}

export default function Perfil({ alGuardar, onCerrar }) {
  /* Se repinta cuando llega el idioma. Sin esto el componente depende
   * de que su padre se repinte por otra razón — hoy pasa, y por eso el
   * bug del 2026-08-25 se veía en unas pantallas y no en otras. */
  useIdioma();
  // `recargar` vuelve a pedir /auth/me. Es lo que hace que la estación
  // de hoy se actualice apenas se elige el país, sin salir y entrar.
  const { plan, recargar, salir } = useAuth();

  // El modal de borrar cuenta. Ver <BorrarCuenta /> al final del archivo.
  const [borrando, setBorrando] = useState(false);

  const [datos, setDatos] = useState(null);
  const [listas, setListas] = useState(null);
  const [borrador, setBorrador] = useState({});
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);

  useEffect(() => {
    Promise.all([api.leerPerfil(), cargarOpciones()])
      .then(([p, o]) => {
        setDatos(p);
        setListas(o);
        setBorrador(p.perfil || {});
      })
      .catch((e) => setError(tError(e)));
  }, []);

  function cambiar(campo, valor) {
    setBorrador((b) => ({ ...b, [campo]: valor }));
    setGuardado(false);
  }

  function alternarEnLista(campo, valor) {
    const actual = borrador[campo] || [];
    cambiar(
      campo,
      actual.includes(valor)
        ? actual.filter((v) => v !== valor)
        : [...actual, valor]
    );
  }

  async function guardar() {
    setGuardando(true);
    setError("");
    try {
      /* ⚠️  SE MIRA SI CAMBIÓ EL GUARDARROPA **ANTES** DE GUARDAR.
       *
       * Después de `guardarPerfil` ya no hay con qué comparar: el
       * borrador y lo guardado dicen lo mismo. Este es exactamente el
       * tipo de detalle que hace que una invalidación de caché "a veces
       * funcione".
       */
      const cambioGuardarropa =
        (borrador.guardarropa || null) !== (datos.perfil?.guardarropa || null);

      await api.guardarPerfil(borrador);
      setGuardado(true);

      /* EL CACHÉ DE OPCIONES TIENE QUE MORIR SI CAMBIÓ EL GUARDARROPA.
       *
       * `src/opciones.js` guarda la taxonomía YA ORDENADA según qué ropa
       * usa el usuario (§15i). Sin esta línea, alguien cambia de "ropa
       * de hombre" a "ropa de mujer", guarda, abre el desplegable de 108
       * prendas y sigue viendo el orden viejo hasta recargar la página.
       *
       * Se ve exactamente igual que "no se guardó" — el reporte que ya
       * llegó tres veces en este proyecto por otras causas.
       *
       * El comentario de la auditoría lo había marcado como frágil: "el
       * día que Perfil edite guardarropa, faltará la invalidación". Ese
       * día es hoy.
       */
      if (cambioGuardarropa) invalidarOpciones();

      // Los ejemplos de estilo que se muestran acá vienen dirigidos al
      // guardarropa guardado, así que se vuelve a leer el perfil para
      // que las tarjetas se actualicen sin salir de la pantalla.
      try {
        const fresco = await api.leerPerfil();
        setDatos(fresco);
      } catch {
        /* que falle el refresco no puede deshacer un guardado que salió
           bien: los datos ya están en la base y se ven al reabrir */
      }

      // El contexto de sesión tiene que enterarse. Guardaba solo contra
      // la API y `estacionActual` —que llega en /auth/me— se quedaba
      // viejo: elegías Paraguay, se guardaba bien, y "Qué me pongo"
      // seguía sin preseleccionar la estación hasta volver a entrar.
      await recargar?.();

      // El armario y las sugerencias cambian con esto: si tocaste la
      // tolerancia al color, las combinaciones que ves ya no son las
      // mismas. Recargar es parte de guardar.
      alGuardar?.();

      /* Y se cierra la ventana.
       *
       * ⚠️  EL ORDEN IMPORTA: `alGuardar` marca `perfilTocado` y
       * `onCerrar` lo LEE para decidir si recarga el armario. Al revés,
       * se cierra sin recargar y los cambios recién se ven al navegar.
       *
       * Va acá adentro del `try` a propósito: si el guardado falló, la
       * ventana tiene que quedar abierta con el error a la vista. Cerrar
       * ante un error es la peor versión de este botón — parece que
       * guardó.
       *
       * `setGuardado(true)` de arriba queda igual: sostiene el "Guardado"
       * cuando esta pantalla se usa sin `onCerrar`. Con la ventana, la
       * confirmación es que se cierre. */
      onCerrar?.();
    } catch (e) {
      setError(tError(e));
    } finally {
      setGuardando(false);
    }
  }

  if (error && !datos) return <div className="mensaje-error" role="alert">{error}</div>;
  if (!datos || !listas) return <p className="tenue">{t("comun.cargando", "Cargando...")}</p>;

  const o = datos.opciones;

  const nombre = (borrador.nombre || "").trim();

  return (
    <section className="perfil">
      {/* El nombre arriba y en grande. Es de quién es este armario, y
          además es lo primero que uno busca confirmar al entrar a un
          perfil. Enterrado como un campo más entre "rango de edad" e
          "idioma" no cumple ninguna de las dos cosas. */}
      <header className="perfil-cabecera">
        <div className="perfil-inicial" aria-hidden="true">
          {(nombre || "?").charAt(0).toUpperCase()}
        </div>
        <div className="perfil-identidad">
          <h1 className="perfil-nombre">{nombre || t("perfil.sinNombre", "Sin nombre")}</h1>
          <label className="perfil-editar-nombre">
            <span className="tenue texto-mini">
              {nombre
                ? t("perfil.cambiarNombre", "Cambiar nombre")
                : t("perfil.comoTeLlamo", "¿Cómo quieres que te llame?")}
            </span>
            <input
              value={borrador.nombre || ""}
              onChange={(e) => cambiar("nombre", e.target.value)}
              placeholder={t("perfil.tuNombre", "Tu nombre")}
              maxLength={60}
            />
          </label>
        </div>
      </header>

      {error && <div className="mensaje-error" role="alert">{error}</div>}
      {guardado && <div className="mensaje-ok">{t("perfil.guardadoOk", "Guardado.")}</div>}

      {/* ---------- Lo que sí cambia el comportamiento ---------- */}

      <Bloque
        titulo={t("perfil.comoSugiero", "Cómo te sugiero la ropa")}
        detalle={t("perfil.comoSugieroDetalle", "Esto cambia las combinaciones que ves. Es lo que más impacto tiene de toda esta pantalla.")}
      >
        {/* ⚠️  `Eleccion` NO TRADUCE, Y ESO ES CORRECTO.
            Es un componente genérico: recibe `{valor, etiqueta, detalle}`
            ya armados y no puede saber de qué grupo vienen. La
            traducción va en el LLAMADOR, que sí lo sabe — es la misma
            lección que dejó `GrupoFiltro` y el punto ciego de los
            valores que llegan por prop. */}
        <Eleccion
          etiqueta={t("perfil.mezclaColores", "Mezcla de colores")}
          opciones={traducirOpciones(o.toleranciaColor, "toleranciaColor")}
          valor={borrador.tolerancia_color || "sobrio"}
          onElegir={(v) => cambiar("tolerancia_color", v)}
        />

        <Eleccion
          etiqueta={t("perfil.mezclaFormalidad", "Mezcla de formalidad")}
          opciones={traducirOpciones(o.toleranciaFormalidad, "toleranciaFormalidad")}
          valor={borrador.tolerancia_formalidad || "normal"}
          onElegir={(v) => cambiar("tolerancia_formalidad", v)}
        />
      </Bloque>

      <Bloque
        titulo={t("perfil.coloresNoUsas", "Colores que no usas")}
        detalle={t("perfil.coloresNoUsasDetalle", "Las prendas de estos colores no van a aparecer en las sugerencias, y no te los voy a recomendar comprar. Es distinto de un veto: el veto es sobre un par de prendas, esto es sobre el color entero.")}
      >
        <div className="opciones-chips">
          {listas.colores.map((c) => (
            <button
              key={c}
              type="button"
              className={`chip ${
                (borrador.colores_evitados || []).includes(c) ? "chip-activo" : ""
              }`}
              onClick={() => alternarEnLista("colores_evitados", c)}
            >
              {tCat("color", c)}
            </button>
          ))}
        </div>
      </Bloque>

      <Bloque
        titulo={t("perfil.paraQueVistes", "Para qué te vistes normalmente")}
        detalle={t("perfil.paraQueVistesDetalle", 'Lo usa "Qué comprar". Sin esto adivino mirando tu armario, y ahí hay un problema circular: si te falta ropa de trabajo, tu armario dice que no trabajas y nunca te sugiero comprarla.')}
      >
        <div className="opciones-chips">
          {listas.categorias.map((c) => (
            <button
              key={c}
              type="button"
              className={`chip ${
                (borrador.ocasiones_habituales || []).includes(c)
                  ? "chip-activo"
                  : ""
              }`}
              onClick={() => alternarEnLista("ocasiones_habituales", c)}
            >
              {tCat("ocasion", c)}
            </button>
          ))}
        </div>
      </Bloque>

      {/* EL PAÍS. No es un dato demográfico: es lo único que permite
          saber qué estación es HOY. En agosto es invierno en Buenos
          Aires y verano en Madrid, así que sin esto la app tendría que
          adivinar — y adivinaría mal para media humanidad.

          Se explica en pantalla para que no parezca que se pregunta
          por preguntar. */}
      <Bloque titulo={t("perfil.dondeVives", "Dónde vives")}>
        <label className="campo campo-pais">
          <span className="tenue texto-mini">{t("perfil.pais", "País")}</span>
          <select
            value={borrador.pais || ""}
            onChange={(e) => cambiar("pais", e.target.value || null)}
          >
            <option value="">{t("perfil.sinEspecificar", "Sin especificar")}</option>
            {(o.paises || []).map((p2) => (
              <option key={p2.codigo} value={p2.codigo}>
                {pais(p2.codigo, p2.nombre)}
              </option>
            ))}
          </select>
          <span className="tenue texto-mini">
            {o.estacionActual
              ? t("perfil.estacionHoy", 'Hoy es {estacion} donde estás: "Qué me pongo" arranca en esa estación.', {
                  /* El valor viene del servidor en español
                   * ("Primavera"): sin `tCat` la frase sale traducida y
                   * la estación adentro en español. Mismo caso que el
                   * ejemplo de nivel en NuevaOcasion. */
                  estacion: tCat("estacion", o.estacionActual).toLowerCase(),
                })
              : borrador.pais
              ? t("perfil.sinEstaciones", "Tu país no tiene cuatro estaciones marcadas, así que la estación la eliges tú cada vez.")
              : t("perfil.paraQueElPais", "Sirve para que la app sepa qué estación es hoy y arranque en la correcta.")}
          </span>
        </label>
      </Bloque>

      {/* ⚠️  ESTOS DOS BLOQUES FALTABAN, Y ERA EL HALLAZGO DE PRODUCTO
          MÁS SERIO DE LA AUDITORÍA (2026-08-24, AL-6).

          El guardarropa y el estilo solo se podían elegir en la
          bienvenida. Y la bienvenida se marca como vista TAMBIÉN al
          saltearla, así que no vuelve nunca.

          O sea: quien la salteó, quien eligió apurado, o quien
          simplemente cambió de idea, perdía PARA SIEMPRE los dos datos
          que más personalizan la app — el orden del desplegable de 108
          prendas, los ejemplos de estilo, y el orden de las
          combinaciones. Sin ninguna pantalla donde arreglarlo.

          Y encima la propia bienvenida decía "Se configura después en
          Perfil", que era falso. Un texto que promete algo que no
          existe es peor que no decir nada.

          A 30.000 usuarios eso no es un caso borde: el porcentaje que
          saltea un onboarding es enorme, y esa fracción entera se
          quedaba sin la mitad del producto sin saberlo. */}
      <Bloque
        titulo={t("perfil.queRopaUsas", "Qué ropa usas")}
        detalle={t("perfil.queRopaUsasDetalle", "Ordena las listas: lo que usas aparece primero. Nada se esconde — vas a poder cargar cualquier prenda igual.")}
      >
        <Eleccion
          etiqueta={t("perfil.tuGuardarropa", "Tu guardarropa")}
          opciones={[
            /* ⚠️  `traducirOpciones` FALTABA ACÁ Y SOLO ACÁ (2026-09-05).
             *
             * Los otros tres `Eleccion` la usan; este pasaba la lista
             * del servidor cruda. Efecto: "Ropa de mujer / Ropa de
             * hombre / Las dos" en español, y la CUARTA opción —la que
             * se arma abajo con `t()`— en el idioma correcto. Media
             * pantalla traducida, que es peor que ninguna: parece un
             * descuido de la traducción y no un bug.
             *
             * Lo reportó el dueño con una captura del perfil en
             * portugués. La excepción que yo había declarado en
             * `test_valores_crudos.js` decía que `Eleccion` recibe
             * opciones ya traducidas — la escribí confiando en el
             * comentario de acá arriba en vez de mirar los cuatro
             * llamadores. Ahora hay un guardián que los mira. */
            ...traducirOpciones(o.guardarropas, "guardarropa"),
            // La opción de no contestar tiene que EXISTIR y ser
            // reversible. Sin esto, alguien que eligió por error queda
            // atrapado en su elección, que es el mismo problema que
            // este bloque vino a resolver.
            {
              valor: "",
              etiqueta: t("perfil.prefieroNoDecir", "Prefiero no decir"),
              detalle: t("perfil.ordenNeutro", "Listas en orden neutro"),
            },
          ]}
          valor={borrador.guardarropa || ""}
          onElegir={(v) => cambiar("guardarropa", v || null)}
        />
      </Bloque>

      <Bloque
        titulo={t("perfil.tuEstilo", "Tu estilo")}
        detalle={t("perfil.tuEstiloDetalle", "Ordena las propuestas de 'Qué me pongo': lo que más habla tu estilo aparece primero. Nunca esconde nada.")}
      >
        <Eleccion
          etiqueta={t("perfil.estiloDominante", "Estilo dominante")}
          opciones={[
            /* ⚠️  LA TRADUCCIÓN DE LOS ESTILOS EXISTÍA Y NADIE LA
                LLAMABA. `estilo.deportivo.nombre` está en los DOCE
                idiomas desde la tanda C4 y `test_i18n` §4 lo verifica —
                pero esta pantalla dibujaba `e.nombre`, que viene del
                servidor en español. Traducción escrita, revisada y
                muerta.

                El respaldo sigue siendo lo que manda el servidor: si
                mañana se agrega un estilo y todavía no tiene clave, se
                ve en español en vez de no verse. */
            ...(o.estilos || []).map((e) => ({
              valor: e.clave,
              etiqueta: t(`estilo.${e.clave}.nombre`, e.nombre),
              /* Los ejemplos concretos son la mitad de la pregunta: sin
                 ellos "Dramático" no significa nada. Son NOMBRES DEL
                 CATÁLOGO ("Camiseta básica", "Jeans"), así que se
                 traducen con tCat como en cualquier otro lado. */
              detalle: (e.ejemplos || []).map((x) => tCat("prenda", x)).join(" · "),
            })),
            {
              valor: "",
              etiqueta: t("perfil.sinDefinir", "Sin definir"),
              detalle: t("perfil.sinOrdenEstilo", "Las propuestas no se ordenan por estilo"),
            },
          ]}
          valor={borrador.estilo_dominante || ""}
          onElegir={(v) =>
            setBorrador((b) => ({
              ...b,
              estilo_dominante: v || null,
              // Sin dominante los acentos no significan nada: el motor
              // los ignora. Dejarlos puestos sería guardar un dato que
              // no hace nada, que es justo lo que este proyecto borró
              // dos veces (§16, notas_estilo y rango_edad).
              estilos_acento: v ? b.estilos_acento || [] : [],
            }))
          }
        />

        {borrador.estilo_dominante && (
          <div className="campo">
            <span className="campo-etiqueta">
              {t("perfil.acentos", "Acentos")}{" "}
              <span className="tenue texto-mini">{t("perfil.acentosNota", "(hasta 2, opcional)")}</span>
            </span>
            <div className="eleccion">
              {(o.estilos || [])
                .filter((e) => e.clave !== borrador.estilo_dominante)
                .map((e) => {
                  const puesto = (borrador.estilos_acento || []).includes(e.clave);
                  // Dos como máximo: es la receta del sistema, un
                  // dominante y uno o dos acentos. Con cuatro no hay
                  // blend, hay lista de deseos.
                  const lleno = (borrador.estilos_acento || []).length >= 2;
                  return (
                    <button
                      key={e.clave}
                      type="button"
                      disabled={!puesto && lleno}
                      className={`eleccion-op ${puesto ? "eleccion-activa" : ""}`}
                      onClick={() => alternarEnLista("estilos_acento", e.clave)}
                    >
                      <strong>{t(`estilo.${e.clave}.nombre`, e.nombre)}</strong>
                      <span className="tenue texto-mini">
                        {(e.ejemplos || []).map((x) => tCat("prenda", x)).join(" · ")}
                      </span>
                    </button>
                  );
                })}
            </div>
          </div>
        )}
      </Bloque>

      {/* Los colores y ocasiones propios se CREAN parado en una prenda
          —que es cuando uno descubre que falta "Fucsia"— pero se BORRAN
          acá. Borrar no es una acción del momento de usar algo: es
          mantenimiento, y va con el resto de la configuración. */}
      <Bloque titulo={t("perfil.misListas", "Mis listas")}>
        {/* `alGuardar` marca el perfil como tocado: borrar un color,
            una ocasión o un veto cambia las sugerencias, y la pantalla
            de atrás tiene que recargarse al cerrar. Iba sin nada. */}
        <MisListas alCambiar={alGuardar} />
      </Bloque>

      {/* ¿Estoy viendo la versión que acabo de subir?
          Pasó dos veces que una función nueva "no aparecía" y las dos
          el código estaba bien. Este bloque distingue de un vistazo
          entre no desplegado, deploy fallado y navegador con copia
          vieja — tres problemas con la misma cara. */}
      {/* ⚠️  EL PLAN VA ANTES DE "VERSIÓN" Y DESPUÉS DE TODO LO DEMÁS.
          No es lo que la persona viene a tocar acá —viene a cambiar
          cómo le sugiero la ropa— pero es lo que necesita encontrar el
          día que se pregunta "¿hasta cuándo tengo esto?". Poner el
          cobro arriba de todo convierte una pantalla de preferencias en
          una pantalla de venta. */}
      {plan && (
        <Bloque titulo={t("perfil.tuPlan", "Tu plan")}>
          <p className="perfil-plan-nombre">
            {!plan.acceso
              ? t("perfil.planVencido", "Tu plan terminó")
              : plan.origen === "prueba"
              ? t("perfil.planPrueba", "Prueba gratuita")
              : plan.origen === "mensual"
              ? t("perfil.planMensual", "Suscripción mensual")
              : plan.origen === "anual"
              ? t("perfil.planAnual", "Suscripción anual")
              : t("perfil.planCortesia", "Acceso de cortesía")}
          </p>

          {/* La fecha solo cuando dice algo. Una cortesía vence en 2099
              y mostrar esa fecha no informa: confunde. */}
          {plan.venceEn && plan.origen !== "cortesia" && (
            <p className="tenue texto-mini">
              {!plan.acceso
                ? t("perfil.planTerminoEl", "Terminó el {fecha}", {
                    fecha: fechaLocal(plan.venceEn),
                  })
                : plan.origen === "prueba"
                ? t("perfil.planTermina", "Termina el {fecha}", {
                    fecha: fechaLocal(plan.venceEn),
                  })
                : t("perfil.planRenueva", "Se renueva el {fecha}", {
                    fecha: fechaLocal(plan.venceEn),
                  })}
            </p>
          )}

          <p className="tenue texto-mini">
            {plan.prendas != null && plan.topePrendas != null
              ? t("perfil.planPrendas", "{n} de {max} prendas", {
                  n: plan.prendas,
                  max: plan.topePrendas,
                })
              : plan.acceso
              ? t("perfil.planSinTope", "Sin tope de prendas")
              : t(
                  "perfil.planVencidoQue",
                  "Sigues viendo, editando, borrando y registrando tu armario. Para agregar prendas nuevas necesitas una suscripción."
                )}
          </p>

          {plan.fallaEn && (
            <p className="mensaje-error texto-mini" role="alert">
              {t("perfil.planCobroFalla", "Tu último cobro falló. Revisa tu medio de pago.")}
            </p>
          )}

          {/* ⚠️  DESACTIVADO A PROPÓSITO, Y NO ES UN PENDIENTE OLVIDADO.
              El cobro todavía no existe. Un botón que promete llevar a
              algún lado y no lleva es peor que no tenerlo; uno que dice
              lo que pasa es honesto. Cuando exista el cobro, se le saca
              el `disabled` y se le pone el destino. */}
          <button className="boton-primario" type="button" disabled>
            {t("perfil.planProximamente", "Suscribirme (próximamente)")}
          </button>
        </Bloque>
      )}

      <Bloque titulo={t("perfil.version", "Versión")}>
        <Version />
      </Bloque>

      <Bloque titulo={t("perfil.idioma", "Idioma")}>
        <label className="campo">
          <span className="tenue texto-mini">{t("perfil.idiomaInterfaz", "Idioma de la interfaz")}</span>
          <select
            value={borrador.idioma || "es"}
            onChange={(e) => cambiar("idioma", e.target.value)}
          >
            {o.idiomas.map((i) => (
              <option key={i.valor} value={i.valor}>
                {i.etiqueta}
              </option>
            ))}
          </select>
          {/* ⚠️  ESTE TEXTO DICE EN QUÉ MITAD ESTAMOS, Y TIENE QUE
              SEGUIR DICIÉNDOLO MIENTRAS SEA VERDAD.

              Antes del 2026-08-25 decía "Guardado, pero la interfaz
              todavía está solo en español" — y era exacto: la columna se
              guardaba y no la leía nadie. Hoy el idioma sí llega al
              módulo que traduce, pero solo está traducido el catálogo
              (los nombres de prenda, colores, materiales, ocasiones).
              Los ~682 textos de la interfaz siguen en español.

              Media traducción avisada es honesta; media traducción
              callada parece un producto roto. */}
          <span className="tenue texto-mini">{avisoDeIdioma(borrador.idioma)}</span>
        </label>
      </Bloque>

      <div className="perfil-guardar">
        <button
          className="boton-primario"
          onClick={guardar}
          disabled={guardando}
        >
          {guardando
            ? t("perfil.guardando", "Guardando...")
            : t("perfil.guardarPreferencias", "Guardar preferencias")}
        </button>
      </div>

      {/* ⚠️  ÚLTIMO DEL TODO, Y SEPARADO. No es estética.
          Es la única acción de esta pantalla que no se puede deshacer, y
          está a un toque de «Guardar preferencias». Va al final, con su
          propio marco, para que nadie la toque yendo hacia otra cosa. */}
      <div className="perfil-zona-riesgo">
        <h2 className="perfil-titulo">{t("perfil.borrarCuenta", "Borrar mi cuenta")}</h2>
        <p className="tenue texto-mini">
          {t(
            "perfil.borrarCuentaExplica",
            "Se borra todo: tus prendas, tus fotos, tus outfits y tu cuenta. No se puede deshacer."
          )}
        </p>
        <button
          className="boton-peligro"
          type="button"
          onClick={() => setBorrando(true)}
        >
          {t("perfil.borrarCuenta", "Borrar mi cuenta")}
        </button>
      </div>

      {borrando && (
        <BorrarCuenta
          onCerrar={() => setBorrando(false)}
          alBorrar={() => {
            /* Se cierra la sesión y listo. No hay a dónde volver: la
               cuenta ya no existe, así que cualquier pantalla que
               intentara pedir algo recibiría 401. */
            setBorrando(false);
            salir();
          }}
        />
      )}
    </section>
  );
}

/* ------------------------------------------------------------------
   El modal de borrar cuenta.

   Dos toques —el botón de arriba y el de acá— y es lo que se decidió.
   Lo que hace que dos toques alcancen es lo que se lee en el medio: la
   lista de lo que desaparece, escrita en concreto y no como "todos tus
   datos". Alguien que llegó hasta acá sin querer borrar nada, lee eso y
   se va.

   ⚠️  El botón destructivo NO es el primario ni está donde cae el
       pulgar. «Cancelar» es el que se ve primero.
------------------------------------------------------------------ */
function BorrarCuenta({ onCerrar, alBorrar }) {
  useIdioma();
  const caja = useFocoAtrapado();
  const [yendo, setYendo] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const alTeclear = (e) => {
      if (e.key === "Escape") onCerrar();
    };
    window.addEventListener("keydown", alTeclear, true);
    return () => window.removeEventListener("keydown", alTeclear, true);
  }, [onCerrar]);

  async function borrar() {
    setYendo(true);
    setError("");
    try {
      await api.borrarCuenta();
      alBorrar();
    } catch (e) {
      /* La cuenta SIGUE existiendo: el servidor corta antes de tocar
         nada si Storage falla. El mensaje lo dice, porque el miedo acá
         es "¿se borró a medias?". */
      setError(tError(e));
      setYendo(false);
    }
  }

  return (
    <div
      className="modal modal-sobre-modal"
      role="dialog"
      aria-modal="true"
      aria-label={t("perfil.borrarCuenta", "Borrar mi cuenta")}
      onClick={onCerrar}
    >
      <div className="modal-caja" ref={caja} onClick={(e) => e.stopPropagation()}>
        <div className="modal-cabecera">
          <h2 className="modal-titulo">
            {t("perfil.borrarCuentaSeguro", "¿Seguro que quieres borrar tu cuenta?")}
          </h2>
        </div>

        <div className="modal-cuerpo">
          <p>
            {t(
              "perfil.borrarCuentaQue",
              "Esto borra, ahora mismo y para siempre: tus prendas, todas tus fotos, tus outfits registrados, tus colores y ocasiones propias, tu maleta y tu cuenta."
            )}
          </p>
          <p className="tenue texto-mini">
            {t("perfil.borrarCuentaSinVuelta", "No se puede deshacer y no hay copia.")}
          </p>

          {error && <div className="mensaje-error" role="alert">{error}</div>}

          <div className="perfil-borrar-botones">
            {/* Cancelar primero, y como primario: es la salida que
                queremos que sea fácil. */}
            <button
              className="boton-primario"
              type="button"
              onClick={onCerrar}
              disabled={yendo}
            >
              {t("perfil.cancelar", "Cancelar")}
            </button>
            <button
              className="boton-peligro"
              type="button"
              onClick={borrar}
              disabled={yendo}
            >
              {yendo
                ? t("perfil.borrandoCuenta", "Borrando...")
                : t("perfil.borrarCuentaConfirmar", "Sí, borrar todo")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Bloque({ titulo, detalle, children, apagado }) {
  return (
    <div className={`perfil-bloque ${apagado ? "perfil-bloque-apagado" : ""}`}>
      <h2 className="perfil-titulo">{titulo}</h2>
      {detalle && <p className="tenue texto-mini perfil-detalle">{detalle}</p>}
      {children}
    </div>
  );
}

/**
 * Una elección entre pocas opciones, cada una con su explicación.
 *
 * No es un select: con tres opciones y una línea de explicación cada
 * una, un desplegable esconde justo lo que hay que leer para elegir.
 */
/* Traduce una lista `{valor, etiqueta, detalle}` que vino del
 * servidor. La clave es `opciones.<grupo>.<valor>` y el respaldo es lo
 * que mandó el servidor: un valor nuevo sin traducir se ve en español
 * en vez de no verse. Ver lib/opciones_api.js. */
function traducirOpciones(lista, grupo) {
  return (lista || []).map((op) => ({
    ...op,
    etiqueta: t(`opciones.${grupo}.${op.valor}`, op.etiqueta),
  }));
}

function Eleccion({ etiqueta, opciones, valor, onElegir }) {
  return (
    <div className="campo">
      <span className="campo-etiqueta">{etiqueta}</span>
      <div className="eleccion">
        {opciones.map((op) => (
          <button
            key={op.valor}
            type="button"
            className={`eleccion-op ${valor === op.valor ? "eleccion-activa" : ""}`}
            onClick={() => onElegir(op.valor)}
          >
            <strong>{op.etiqueta}</strong>
            <span className="tenue texto-mini">{op.detalle}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
